import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import useContext from '../../../../hooks/useContext';
import useToggle from '../../../../hooks/useToggle';
import Panel from '../../../../styled/Panel';
import ReadAboutButton from '../../../../styled/ReadAboutButton';
import { ProductContext } from '../providers/ProductProvider';
import ArticleInformation from './ArticleInformation';
import Documents from './Documents';
import GenericInformation from './GenericInformation';
import CertificationInformation from './CertificationInformation';
import DesignerInformation from './DesignerInformation';
import { useTrackGenericEvent } from '../../../../hooks/tracking/useTrackingEvents';
import useCompose from '../../../../hooks/useCompose';
import ReviewsProductPage, { useReviewContext } from '../../../../components-legacy/Review';

interface IProductInfoItem {
  children: ReactNode;
  title: string;
  eventName: string;
}

const ProductInfoItem = ({ children, eventName, title }: IProductInfoItem) => {
  const [showPanel, toggleShowPanel] = useToggle(false);
  const trackingEvent = useMemo(() => ({ event: eventName }), [eventName]);
  const trackClick = useTrackGenericEvent(trackingEvent);
  const clickHandler = useCompose(trackClick, toggleShowPanel);
  const { interactWithReviewBadge } = useReviewContext();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (title.includes('Recensioner')) {
      toggleShowPanel();
    }
  }, [interactWithReviewBadge]);

  return (
    <>
      <ReadAboutButton onClick={clickHandler} isPanelExpanded={showPanel}>
        {title}
      </ReadAboutButton>
      {showPanel && (
        <Panel title={title} onClose={toggleShowPanel}>
          {children}
        </Panel>
      )}
    </>
  );
};

const Specifications = ({ hasReviews }: { hasReviews: boolean }) => {
  const {
    designer,
    documents,
    richSpecifications,
    productCertifications,
    groupId,
    moderArtikelId,
  } = useContext(ProductContext);

  return (
    <>
      <ProductInfoItem title="Produktbeskrivning" eventName="product_description_click">
        <ArticleInformation />
      </ProductInfoItem>
      {richSpecifications &&
        Object.keys(richSpecifications)
          .filter((s) => !!s)
          .map((group) => (
            <ProductInfoItem
              title={group}
              key={group}
              eventName={`product_${group.toLowerCase()}_click`}>
              <GenericInformation sections={richSpecifications[group as keyof ISpecifications]} />
            </ProductInfoItem>
          ))}
      {!!documents?.length && (
        <ProductInfoItem title="Dokument" eventName="product_documents_click">
          <Documents items={documents} />
        </ProductInfoItem>
      )}
      {hasReviews && (
        <ProductInfoItem title={`Recensioner`} eventName="product_reviews_click">
          <ReviewsProductPage {...{ moderArtikelId, groupId }} />
        </ProductInfoItem>
      )}
      {!!productCertifications?.length && (
        <ProductInfoItem
          title="Miljö och kvalitetsmärkningar"
          eventName="product_certificates_click">
          <CertificationInformation productCertifications={productCertifications} />
        </ProductInfoItem>
      )}
      {designer && (
        <ProductInfoItem title="Designer" eventName="product_designer_click">
          <DesignerInformation designer={designer} />
        </ProductInfoItem>
      )}
    </>
  );
};

export default Specifications;
